import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Package as PackageIcon, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Compact section shown inside PatientProfileModal listing the patient's packages
 * (default: active only, with a toggle to see completed/cancelled/closed too).
 */
const PatientPackagesSection = ({ patientId, user }) => {
  const isAdmin = user?.role === 'Admin';
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      const params = { patient_id: patientId, limit: 50 };
      if (!showAll) params.status = 'active';
      const res = await api.get('/packages', { params });
      setRows(res.data?.rows || []);
    } catch (e) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, [patientId, showAll]);

  useEffect(() => { load(); }, [load]);

  if (!patientId) return null;

  const activeCount = rows.filter(p => p.status === 'active').length;

  return (
    <div data-testid="patient-packages-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <PackageIcon className="w-5 h-5 text-purple-600" />
          Packages
          {rows.length > 0 && (
            <Badge className="bg-purple-100 text-purple-700 text-xs">{activeCount} active</Badge>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAll(v => !v)}
            data-testid="patient-packages-toggle-all"
            className="h-7 text-xs"
          >
            {showAll ? 'Active only' : 'Show all'}
          </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            className="h-7"
            data-testid="patient-packages-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {!loading && rows.length === 0 && (
        <div className="text-center py-4 text-sm text-gray-500 border border-dashed border-gray-200 rounded-xl" data-testid="patient-packages-empty">
          No {showAll ? '' : 'active '}packages for this patient.
        </div>
      )}

      <div className="space-y-2">
        {rows.map(pkg => {
          const remaining = (pkg.items || []).reduce((s, it) => s + (Number(it.quantity_total || 0) - Number(it.quantity_consumed || 0)), 0);
          const totalQty = (pkg.items || []).reduce((s, it) => s + Number(it.quantity_total || 0), 0);
          const isOpen = expandedId === pkg.id;
          const statusCls = {
            active: 'bg-emerald-100 text-emerald-700',
            completed: 'bg-blue-100 text-blue-700',
            cancelled: 'bg-rose-100 text-rose-700',
            closed: 'bg-gray-200 text-gray-700',
          }[pkg.status] || 'bg-gray-100 text-gray-700';
          return (
            <div key={pkg.id} className="bg-white border border-purple-100 rounded-xl overflow-hidden" data-testid={`patient-package-${pkg.ref_number}`}>
              <button
                type="button"
                onClick={() => setExpandedId(isOpen ? null : pkg.id)}
                className="w-full p-3 hover:bg-purple-50/40 flex items-center gap-3 text-left"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded">{pkg.ref_number}</span>
                    <Badge className={`${statusCls} text-[10px] capitalize`}>{pkg.status}</Badge>
                    <span className="text-[11px] text-gray-500">
                      {String(pkg.created_at || '').slice(0, 10)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 mt-0.5">
                    <span className="font-semibold">{remaining}/{totalQty}</span> credits left
                    {pkg.total_cost != null && (
                      <>
                        {' · '}
                        <span>₹{Number(pkg.total_paid || 0).toLocaleString()}/{Number(pkg.total_cost || 0).toLocaleString()}</span>
                        {Number(pkg.balance || 0) > 0 && <span className="text-orange-600"> · Bal ₹{Number(pkg.balance).toLocaleString()}</span>}
                      </>
                    )}
                  </p>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-0.5">
                  {(pkg.items || []).map((it, i) => (
                    <div key={i} className="flex justify-between text-xs text-gray-700">
                      <span className="truncate pr-1">{it.service_name}</span>
                      <span className="font-mono text-gray-500">{it.quantity_consumed || 0}/{it.quantity_total}</span>
                    </div>
                  ))}
                  {pkg.notes && (
                    <p className="text-[11px] text-gray-500 italic mt-2">{pkg.notes}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PatientPackagesSection;
